import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { loadBookDetails, loadCatalog, loadStats, statsToMap } from '@/lib/dataSource'
import type { BookDetails, BookStats, Subject } from '@/lib/types'
import {
  applyDescriptions,
  applyDownloadCounts,
  buildSearchIndex,
  type SearchIndex,
} from './searchIndex'

export const catalogQueryKey = ['catalog'] as const
export const statsQueryKey = ['stats'] as const
export const bookDetailsQueryKey = ['bookDetails'] as const

/**
 * The slim catalogue is immutable between admin edits, so it is never refetched
 * on focus; the version check on load is what picks up a change (§15.3).
 */
export function useCatalog() {
  return useQuery({
    queryKey: catalogQueryKey,
    queryFn: loadCatalog,
    staleTime: 5 * 60_000,
    gcTime: 60 * 60_000,
    refetchOnWindowFocus: false,
  })
}

/**
 * The detail half of every record. Immutable between admin edits like the
 * catalogue, and deliberately a second request: together they would blow the
 * §15.5 first-paint budget. Nothing on a list screen blocks on it.
 */
export function useBookDetails() {
  return useQuery({
    queryKey: bookDetailsQueryKey,
    queryFn: loadBookDetails,
    staleTime: 5 * 60_000,
    gcTime: 60 * 60_000,
    refetchOnWindowFocus: false,
  })
}

/** The detail fields for one title, once they have arrived. */
export function useBookDetail(bookId: string): BookDetails | undefined {
  const { data } = useBookDetails()
  return data?.[bookId]
}

/** Counters and ratings change, so they refresh on their own 60-second cadence. */
export function useStats() {
  return useQuery({
    queryKey: statsQueryKey,
    queryFn: () => loadStats(),
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
}

export function useStatsMap(): Map<string, BookStats> {
  const { data } = useStats()
  return useMemo(() => statsToMap(data ?? []), [data])
}

/** Builds the search index once per catalogue version, not once per keystroke. */
export function useSearchIndex(): SearchIndex | null {
  const { data: catalog } = useCatalog()
  const { data: details } = useBookDetails()
  const stats = useStatsMap()

  const index = useMemo(
    () => (catalog ? buildSearchIndex(catalog.books, catalog.subjects) : null),
    [catalog],
  )

  return useMemo(() => {
    if (!index) return null
    applyDownloadCounts(
      index,
      new Map([...stats].map(([id, s]) => [id, s.downloadsTotal + s.externalOpens])),
    )
    // Search works without these and improves when they land (§10.2).
    if (details) {
      applyDescriptions(
        index,
        new Map(Object.entries(details).map(([id, d]) => [id, d.description ?? ''])),
      )
    }
    // A fresh object rather than the one just mutated. Everything downstream
    // memoises on the index's identity, so returning the same reference left
    // result lists computed before the descriptions arrived — the same query
    // answered 37 books when it came in through the URL and 41 when it was
    // typed a moment later. The copy is two references wide; the entries are
    // shared.
    return { ...index }
  }, [index, details, stats])
}

/** Subject names fall back to English when a translation is missing (FR-7.6). */
export function subjectName(subject: Subject, language: string): string {
  if (language === 'ru' && subject.nameRu) return subject.nameRu
  if (language === 'uz' && subject.nameUz) return subject.nameUz
  return subject.nameEn
}

export function subjectDescription(subject: Subject, language: string): string | null {
  if (language === 'ru' && subject.descriptionRu) return subject.descriptionRu
  if (language === 'uz' && subject.descriptionUz) return subject.descriptionUz
  return subject.descriptionEn ?? null
}

export function useSubjectNamer(): (subject: Subject) => string {
  const { i18n } = useTranslation()
  const language = i18n.resolvedLanguage ?? 'en'
  return useMemo(() => (subject: Subject) => subjectName(subject, language), [language])
}

export function useSubjectsById(): Map<string, Subject> {
  const { data } = useCatalog()
  return useMemo(() => new Map((data?.subjects ?? []).map((s) => [s.id, s])), [data])
}
